/**
 * The filter selection, which lives in the URL.
 *
 * Nothing is kept only in React state that a reload should survive (§10). A
 * person will want two tabs open comparing two selections, and will want to
 * send someone a link to the one they are looking at.
 */
import { useCallback, useMemo } from 'react'
import { useSearch, useLocation } from 'wouter'

export const DIMENSIONS = [
  'sector', 'industry', 'symbol', 'docType', 'period', 'year', 'locale', 'quality',
] as const

export type Dimension = (typeof DIMENSIONS)[number]
export type Selection = Record<Dimension, string[]>

export const EMPTY: Selection = {
  sector: [], industry: [], symbol: [], docType: [],
  period: [], year: [], locale: [], quality: [],
}

/**
 * The cascade order. Changing a level prunes the levels below it -- a
 * selection that no longer matches is removed rather than silently ignored,
 * which is how a filter loses someone's trust.
 */
export const CASCADE: Dimension[] = ['sector', 'industry', 'symbol']

export function parseSelection(search: string): Selection {
  const sp = new URLSearchParams(search)
  const out = { ...EMPTY }
  for (const d of DIMENSIONS) {
    const vals = sp.getAll(d).flatMap((v) => v.split(',')).map((v) => v.trim()).filter(Boolean)
    out[d] = [...new Set(vals)]
  }
  return out
}

export function selectionToSearch(sel: Selection): string {
  const sp = new URLSearchParams()
  for (const d of DIMENSIONS) for (const v of sel[d]) sp.append(d, v)
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const isEmptySelection = (sel: Selection): boolean =>
  DIMENSIONS.every((d) => sel[d].length === 0)

export function countSelected(sel: Selection): number {
  return DIMENSIONS.reduce((n, d) => n + sel[d].length, 0)
}

export function useSelection(): {
  selection: Selection
  search: string
  /** Replace-navigate: rapid filter changes must not fill the back button. */
  set: (next: Selection) => void
  toggle: (dimension: Dimension, value: string) => void
  setDimension: (dimension: Dimension, values: string[]) => void
  clear: () => void
} {
  const search = useSearch()
  const [location, navigate] = useLocation()
  const selection = useMemo(() => parseSelection(search), [search])

  const set = useCallback(
    (next: Selection) => {
      navigate(`${location}${selectionToSearch(next)}`, { replace: true })
    },
    [location, navigate],
  )

  const setDimension = useCallback(
    (dimension: Dimension, values: string[]) => {
      set({ ...selection, [dimension]: [...new Set(values)] })
    },
    [selection, set],
  )

  const toggle = useCallback(
    (dimension: Dimension, value: string) => {
      const cur = selection[dimension]
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
      set({ ...selection, [dimension]: next })
    },
    [selection, set],
  )

  const clear = useCallback(() => navigate(location, { replace: true }), [location, navigate])

  return { selection, search: selectionToSearch(selection), set, toggle, setDimension, clear }
}

/** Debounce a value. The filter query is issued 150ms after the last change (§10.1). */
import { useEffect, useState } from 'react'

export function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}
